import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import Tabs from "@/components/Tabs";
import { useNavigate, Path } from "@/router";
import { useLocation, useNavigate as useRouterNavigate } from "react-router-dom";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import useFacilitiesQuery from "./_hooks/useFacilitiesQuery";
import FacilitiesTable from "./_components/FacilitiesTable";
import { DOMAIN, PERMISSION, useHasCapabilityBool, useRequireCapability } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import type { Action } from "@/components/MenuButton";

export default function FacilitiesPage() {
  useRequireCapability({ domain: DOMAIN.FACILITIES, permission: PERMISSION.viewFacilities });
  usePageTitle("/facilities", "Facilities");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const routerNavigate = useRouterNavigate();
  const tableProps = useFacilitiesQuery();
  const { Menu, setMenu } = useTableRowMenu();
  const flags = useFlags();
  const bulkSelection = useBulkSelection();
  const canCreateFacility = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.createFacilities
  });
  const canManageFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.manageFacilities
  });
  const canManageMergePurge = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageMergePurge
  });
  const isFacilityMergePurgeEnabled =
    flags["backoffice-facility-merge-purge"] === true && canManageFacilities && canManageMergePurge;
  const pageActions: Action[] = canCreateFacility
    ? [
        {
          text: "Create Facility",
          iconName: "add",
          testId: "create-facility-button",
          onClick: () => routerNavigate("/facilities/new")
        }
      ]
    : [];

  return (
    <AppLayout bottomBar>
      <Tabs
        tabItems={[{ label: "Facilities", to: "/facilities" as Path }].map(({ label, to }) => ({
          label,
          onClick: () => navigate(to),
          active: (pathname.endsWith("/") ? pathname.slice(0, -1) : pathname) === to
        }))}
      />
      <Menu />
      <div className="w-full">
        <div className="pt-12" />
        <FacilitiesTable
          {...tableProps}
          actions={pageActions}
          setMenu={setMenu}
          selectedSectionIds={tableProps.selectedSectionIds}
          setSelectedSectionIds={tableProps.setSelectedSectionIds}
          bulkSelection={isFacilityMergePurgeEnabled ? bulkSelection : undefined}
        />
      </div>
    </AppLayout>
  );
}
